import { resolveApiBase } from './api'

export interface ChatMessage {
  role: 'user' | 'assistant'
  content: string
}

export async function streamChat(
  messages: ChatMessage[],
  onText: (chunk: string) => void,
  onDone: (sessionId?: number) => void,
  onError: (msg: string) => void,
  signal?: AbortSignal,
  sessionId?: number | null
): Promise<void> {
  const API_BASE = resolveApiBase(window.location.pathname)

  let response: Response
  try {
    response = await fetch(`${API_BASE}/chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ messages, session_id: sessionId ?? null }),
      signal,
    })
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') return
    onError('Failed to connect. Please try again.')
    return
  }

  if (!response.ok) {
    if (response.status === 429) {
      onError('Too many messages. Please wait a moment.')
    } else {
      onError('Failed to connect. Please try again.')
    }
    return
  }

  const reader = response.body!.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  try {
    let reading = true
    while (reading) {
      const { done, value } = await reader.read()
      if (done) {
        reading = false
        continue
      }

      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n')
      buffer = lines.pop() || ''

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue
        const jsonStr = line.slice(6)
        if (!jsonStr) continue

        try {
          const data = JSON.parse(jsonStr)
          if (data.type === 'text') {
            onText(data.text)
          } else if (data.type === 'done') {
            onDone(data.session_id)
          } else if (data.type === 'error') {
            onError(data.message)
          }
        } catch {
          // skip malformed JSON lines
        }
      }
    }
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') return
    onError('Connection lost. Please try again.')
  } finally {
    reader.releaseLock()
  }
}

export async function recordSessionFeedback(
  sessionId: number,
  resolution: 'solved' | 'unresolved',
): Promise<void> {
  const API_BASE = resolveApiBase(window.location.pathname)
  const res = await fetch(`${API_BASE}/chat/sessions/${sessionId}/feedback`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ resolution }),
  })
  if (!res.ok) {
    throw new Error(`Failed to record chat feedback (HTTP ${res.status})`)
  }
}
